import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["map", "sidebar"]
  static values = { places: Array, nearbyGroups: Array, centerLocation: Object, iconUrl: String }

  connect() {
    try {
      if (typeof(google) != undefined) {
        // Let's wait 0.5 second for the Google Maps API to load
        setTimeout(() => {
          this.initializeMap()
        }, 500)
      }
    } catch (err) {
      console.error("Google Maps failed to load", err)
    }
  }

  initializeMap() {
    this.map()
    this.addMarkers() // Show places on the map
    this.centerMapOnLoad() // Center map on load based on center location
    this.updateSidebarOnLoad() // Load the nearby groups into the sidebar
  }

  map() {
    if (this._map == undefined) {
      const darkStyle = [
        {
          "elementType": "geometry",
          "stylers": [{"color": "#e8f5e9"}]
        },
        {
          "elementType": "labels.icon",
          "stylers": [{"visibility": "off"}]
        },
        {
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#616161"}]
        },
        {
          "elementType": "labels.text.stroke",
          "stylers": [{"color": "#f5f5f5"}]
        },
        {
          "featureType": "administrative.land_parcel",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#bdbdbd"}]
        },
        {
          "featureType": "poi",
          "elementType": "geometry",
          "stylers": [{"color": "#eeeeee"}]
        },
        {
          "featureType": "poi",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#757575"}]
        },
        {
          "featureType": "poi.park",
          "elementType": "geometry",
          "stylers": [{"color": "#c8e6c9"}]
        },
        {
          "featureType": "poi.park",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#6b9a76"}]
        },
        {
          "featureType": "road",
          "elementType": "geometry",
          "stylers": [{"color": "#ffffff"}]
        },
        {
          "featureType": "road.arterial",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#757575"}]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry",
          "stylers": [{"color": "#dadada"}]
        },
        {
          "featureType": "road.highway",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#616161"}]
        },
        {
          "featureType": "road.local",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#9e9e9e"}]
        },
        {
          "featureType": "transit.line",
          "elementType": "geometry",
          "stylers": [{"color": "#e5e5e5"}]
        },
        {
          "featureType": "transit.station",
          "elementType": "geometry",
          "stylers": [{"color": "#eeeeee"}]
        },
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{"color": "#bbdefb"}]
        },
        {
          "featureType": "water",
          "elementType": "labels.text.fill",
          "stylers": [{"color": "#9e9e9e"}]
        }
      ]

      this._map = new google.maps.Map(this.mapTarget, {
        center: new google.maps.LatLng(55.3781, -3.4360), // Default center (UK)
        zoom: 6,
        maxZoom: 13,
        minZoom: 3,
        mapTypeControl: false,
        streetViewControl: false,
        keyboardShortcuts: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        styles: darkStyle
      })
    }
    console.log("Map connected")
    return this._map
  }

  addMarkers() {
    this.placesValue.forEach(place => {
      let marker = new google.maps.Marker({
        position: new google.maps.LatLng(place.latitude, place.longitude),
        map: this._map,
        title: `${place.groups_count} groups in ${place.name}`,
        icon: {
          url: this.iconUrlValue,
          scaledSize: new google.maps.Size(30, 30) // Resize the icon if needed
        }
      })

      marker.addListener('click', () => {
        this.centerMapOnMarker(marker)
        this.queryNearbyGroups(place.latitude, place.longitude, place.name)
      })
    })
  }

  centerMapOnMarker(marker) {
    this._map.setCenter(marker.getPosition())
    this._map.setZoom(7)
  }

  centerMapOnLoad() {
    if (this.centerLocationValue) {
      const { latitude, longitude } = this.centerLocationValue
      this._map.setCenter(new google.maps.LatLng(latitude, longitude))
      this._map.setZoom(7)
    }
  }

  updateSidebarOnLoad() {
    if (this.nearbyGroupsValue && this.centerLocationValue.name) {
      this.updateSidebar(this.nearbyGroupsValue, this.centerLocationValue.name)
    }
  }

  queryNearbyGroups(latitude, longitude, placeName) {
    const url = `/groups?place=${encodeURIComponent(placeName)}`

    fetch(url, {
      headers: { 'Accept': 'application/json' }
    })
        .then(response => response.json())
        .then(data => {
          this.updateSidebar(data.nearby_groups, placeName)
        })
        .catch(error => console.error("Error fetching nearby groups:", error))

    history.pushState(null, null, url)
  }

  updateSidebar(nearbyGroups, placeName) {
    let listTitleElement = document.getElementById("group-list-title")
    let groupListElement = document.getElementById("group-list")
    
    if (groupListElement) {
      // Show the group list
      groupListElement.classList.remove("hidden")
      
      let groupList = nearbyGroups.map(group => `
        <div class="p-4 bg-zinc-800/50 rounded-lg mb-3">
          <div class="flex items-center gap-2 mb-2">
            <a href="/groups/${group.slug}" class="text-white font-medium hover:text-zinc-300 transition-colors">${group.name}</a>
            <i data-lucide="external-link" class="w-4 h-4 text-zinc-400"></i>
          </div>
          <p class="text-sm text-zinc-400">${group.summary}</p>
        </div>
      `).join('')

      const groupWord = nearbyGroups.length === 1 ? 'group' : 'groups';
      listTitleElement.innerHTML = `${nearbyGroups.length} ${groupWord} near <span class="text-white font-medium underline">${placeName}</span>`;
      groupListElement.innerHTML = groupList;
      
      // Reinitialize Lucide icons for the new content
      lucide.createIcons();
    } else {
      console.error("Group list element not found")
    }
  }
}
